import { BaseApiClient } from './base-client';

export interface LinuxUserInfo {
  username: string;
  service_id: number;
  service_name: string;
  parent_username: string;
  permissions: {
    view_shares: boolean;
    create_web_shares: boolean;
    download_vpn: boolean;
    backup_files: boolean;
    view_service_info: boolean;
    manage_own_password: boolean;
  };
}

export interface LinuxUserServiceInfo {
  id: number;
  name: string;
  status: string;
  product_id: number;
  product_name: string;
  created_at: string;
  expires_at: string;
  datacenter_name: string;
  server_name: string;
  container_id: string;
  disk_space: number;
  bandwidth: number;
}

export interface LinuxUserShare {
  name: string;
  path: string;
  type: 'nfs' | 'samba';
  permissions: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export class LinuxUserApiClient extends BaseApiClient {
  async getMe(): Promise<LinuxUserInfo> {
    return this.get<LinuxUserInfo>('/api/v1/api/linux-user/me');
  }

  async getService(): Promise<LinuxUserServiceInfo> {
    return this.get<LinuxUserServiceInfo>('/api/v1/api/linux-user/service');
  }

  async getShares(): Promise<{ shares: LinuxUserShare[] }> {
    return this.get<{ shares: LinuxUserShare[] }>('/api/v1/api/linux-user/shares');
  }

  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await this.post('/api/v1/api/linux-user/change-password', data);
  }

  async downloadVpnConfig(): Promise<Blob> {
    const response = await this.axios.get('/api/v1/api/linux-user/vpn-config/download', {
      responseType: 'blob'
    });
    return response.data;
  }

  // Linux user login - uses OAuth2 password flow
  async linuxUserLogin(username: string, password: string, serviceId: string): Promise<{ access_token: string }> {
    // This endpoint is exempt from CSRF protection
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    params.append('grant_type', 'password');
    params.append('client_id', serviceId); // Service ID passed in client_id field
    
    const response = await this.axios.post<{ access_token: string }>(
      '/api/v1/auth/linux-user/login',
      params,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    );
    
    if (response.data.access_token) {
      // Store the Linux user token separately
      localStorage.setItem('linux_user_token', response.data.access_token);
    }
    
    return response.data;
  }

  // Override getAuthToken for Linux user context
  getAuthToken(): string | null {
    // Check if we're in Linux user context
    const linuxUserToken = localStorage.getItem('linux_user_token');
    if (linuxUserToken && window.location.pathname.startsWith('/linux-user')) {
      return linuxUserToken;
    }
    // Otherwise use normal auth token
    return super.getAuthToken();
  }
}